import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigationStore } from 'store';
import { Stack, Button } from '@mui/material';
import { FAIcon } from '@packages/ui-components';

const INITIAL_COUTER = 30;
const INNER_COUTNER = 10;

const ToastMsg = ({
  resetTimer,
  translation,
  rtl,
}: {
  resetTimer: () => void;
  translation: any;
  rtl?: boolean;
}) => {
  const [innerCounter, setInnerCounter] = useState(INNER_COUTNER);

  useEffect(() => {
    const interval = setInterval(() => {
      if (innerCounter === 0) return;
      setInnerCounter((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innerCounter]);

  const resetInnerTimer = () => {
    setInnerCounter(INITIAL_COUTER);
    resetTimer();
  };

  useEffect(() => {
    const iframe = document.body.querySelector('iframe');
    const iframeBody = iframe?.contentWindow?.document.body;
    const iframeCanvas = iframeBody?.querySelector('canvas');

    // Add event listeners
    window.addEventListener('mousemove', resetInnerTimer);
    window.addEventListener('click', resetInnerTimer);
    window.addEventListener('touchstart', resetInnerTimer);
    window.addEventListener('keydown', resetInnerTimer);
    iframeCanvas?.addEventListener('mousemove', resetInnerTimer);
    iframeCanvas?.addEventListener('click', resetInnerTimer);
    iframeCanvas?.addEventListener('touchstart', resetInnerTimer);

    // Cleanup function to remove event listeners
    return () => {
      window.removeEventListener('mousemove', resetInnerTimer);
      window.removeEventListener('click', resetInnerTimer);
      window.removeEventListener('touchstart', resetInnerTimer);
      window.removeEventListener('keydown', resetInnerTimer);
      iframeCanvas?.removeEventListener('mousemove', resetInnerTimer);
      iframeCanvas?.removeEventListener('click', resetInnerTimer);
      iframeCanvas?.removeEventListener('touchstart', resetInnerTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty array ensures that effect is only run on mount and unmount

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      p={0}
      m={0}
      sx={{ direction: rtl ? 'rtl' : 'ltr' }}
    >
      {`${translation?.navResetIn || 'Resetting Kiosk in '} ${innerCounter} ${translation?.navSeconds || ' seconds'}`}
      <Button
        variant="contained"
        color="primary"
        onClick={resetInnerTimer}
        sx={{ width: '44px', height: '44px', minWidth: 'unset' }}
      >
        <FAIcon icon="faClock" />
      </Button>
    </Stack>
  );
};

export const useScreenCounter = ({
  resetNavigation,
  isBackgroundVisible,
  inAutoNavigate,
  rtl,
}: {
  resetNavigation: (caller: string) => void;
  isBackgroundVisible: boolean;
  inAutoNavigate: boolean;
  rtl: boolean;
}) => {
  const [counter, setCounter] = useState(INITIAL_COUTER);
  const showToast = useNavigationStore((state) => state.navigationToast);
  const setShowToast = useNavigationStore((state) => state.setNavigationToast);
  const translation = useNavigationStore((state) => state.navigationTranslation);

  const resetTimer = () => {
    setCounter(INITIAL_COUTER);
  };

  useEffect(() => {
    if (showToast) {
      toast(<ToastMsg rtl={rtl} resetTimer={resetTimer} translation={translation} />, {
        autoClose: false,
        position: 'top-right',
        hideProgressBar: true,
        closeButton: false,
        closeOnClick: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast]);

  useEffect(() => {
    let interval;
    if (!isBackgroundVisible && !inAutoNavigate) {
      interval = setInterval(() => {
        if (counter === 0) return;
        if (counter <= 10) {
          setShowToast(true);
        } else {
          setShowToast(false);
          toast.dismiss();
        }
        setCounter((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counter, isBackgroundVisible, inAutoNavigate]);

  useEffect(() => {
    const iframe = document.body.querySelector('iframe');
    const iframeBody = iframe?.contentWindow?.document.body;
    const iframeCanvas = iframeBody?.querySelector('canvas');

    // Add event listeners
    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('click', resetTimer);
    window.addEventListener('touchstart', resetTimer);
    window.addEventListener('keydown', resetTimer);
    iframeCanvas?.addEventListener('mousemove', resetTimer);
    iframeCanvas?.addEventListener('click', resetTimer);
    iframeCanvas?.addEventListener('touchstart', resetTimer);

    // Cleanup function to remove event listeners
    return () => {
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('click', resetTimer);
      window.removeEventListener('touchstart', resetTimer);
      window.removeEventListener('keydown', resetTimer);
      iframeCanvas?.removeEventListener('mousemove', resetTimer);
      iframeCanvas?.removeEventListener('click', resetTimer);
      iframeCanvas?.removeEventListener('touchstart', resetTimer);
      toast.dismiss();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty array ensures that effect is only run on mount and unmount

  useEffect(() => {
    if (counter === 0) {
      toast.dismiss();
      resetNavigation('fromKioskTimer');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counter]);

  return { resetTimer, counter };
};
